import { useMemo } from "react";
import { useFilter } from "./useFilter";
import { getFilterConfig, getPresetFilters } from "../configs/filterConfigs";

/**
 * Unified Page Filter Hook
 * 
 * Combines all filter-related setup in one place.
 * No more repeating module names and configurations!
 * 
 * @param {string} moduleName - Module name for filter persistence
 * @param {Function} applyCallback - Callback from useCommonFetch's applyFilters
 * @param {Function} clearCallback - Callback from useCommonFetch's clearFilters
 * @param {Object} config - Filter configuration
 * @param {Array|string} config.filters - Array of filter names OR preset name
 * @param {Object} config.options - Dynamic options for filters (e.g., departmentOptions)
 * @param {Object} config.overrides - Override default filter configurations
 * @param {boolean} config.usePreset - Whether to use preset (default: false)
 * @param {Function} config.onRefreshOptions - Callback to refresh filter options
 * 
 * @returns {Object} Everything needed for PageTableHeader filterProps
 * 
 * @example
 * // Simple usage with filter names
 * const filterProps = usePageFilter(
 *   "designation",
 *   applyFilters,
 *   clearFilters,
 *   {
 *     filters: ['department', 'status'],
 *     options: { departmentOptions: departmentList },
 *     onRefreshOptions: () => refresh('departments')
 *   }
 * );
 * 
 * @example
 * // Using preset
 * const filterProps = usePageFilter(
 *   "staffDirectory",
 *   applyFilters,
 *   clearFilters,
 *   {
 *     filters: 'staff', // preset name
 *     usePreset: true,
 *     options: {
 *       departmentOptions: departmentList,
 *       designationOptions: designationList,
 *       branchOptions: branchList,
 *     }
 *   }
 * );
 */
export const usePageFilter = (
  moduleName,
  applyCallback,
  clearCallback,
  config = {}
) => {
  const {
    filters: filterNames = [],
    options = {},
    overrides = {},
    usePreset = false,
    onRefreshOptions = null,
  } = config;

  // Get filter state management
  const {
    filters,
    setFilter,
    applyFilters: applyFilterState,
    clearFilters: clearFilterState,
    hasActiveFilters,
    activeFilterCount,
  } = useFilter(moduleName, applyCallback, clearCallback);

  // Get filter fields configuration
  const filterFields = useMemo(() => {
    if (!filterNames || (Array.isArray(filterNames) && filterNames.length === 0)) {
      return [];
    }

    if (usePreset) {
      // Use preset filter configuration
      return getPresetFilters(filterNames, options, overrides);
    } else {
      // Use individual filter names
      return getFilterConfig(filterNames, options, overrides);
    }
  }, [filterNames, options, overrides, usePreset]);

  // Return everything needed for PageTableHeader
  return {
    // For PageTableHeader filterProps (spread this directly)
    filterProps: {
      filterModule: moduleName,
      filterFields: filterFields,
      filters: filters,
      setFilter: setFilter,
      onFilterChange: applyFilterState,
      onClearFilters: clearFilterState,
      onRefreshOptions: onRefreshOptions, // ✨ Add refresh callback
    },

    // Additional utilities (if needed)
    filters,
    setFilter,
    applyFilters: applyFilterState,
    clearFilters: clearFilterState,
    hasActiveFilters,
    activeFilterCount,
    filterFields,
  };
};

export default usePageFilter;

